import axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { api } from '../../Api';
import { Store } from '../../Store';
import { getError } from '../../utils';

const fmt = (value, digits = 2) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const fmtDate = (value) => {
  if (!value) return 'Not saved';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not saved';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const emptyLedgerFilters = {
  search: '',
  receiverUsername: '',
  sourceUsername: '',
  status: '',
  generation: '',
  startDate: '',
  endDate: '',
};

const emptyLogFilters = {
  search: '',
  username: '',
  action: '',
  startDate: '',
  endDate: '',
};

function normalizeLevels(levels = [], generationCount = 5, perPersonPercent = 2) {
  const count = Math.min(Math.max(parseInt(generationCount) || 5, 1), 20);
  return Array.from({ length: count }, (_, index) => {
    const generation = index + 1;
    const existing = levels.find((level) => Number(level.generation) === generation);
    return {
      generation,
      label: existing?.label || `Person ${generation}`,
      percent: existing?.percent ?? Number(perPersonPercent || 0),
      isActive: existing?.isActive !== false,
    };
  });
}

function equalLevels(generationCount, perPersonPercent = 2) {
  const count = Math.min(Math.max(parseInt(generationCount) || 5, 1), 20);
  return Array.from({ length: count }, (_, index) => {
    const generation = index + 1;
    return {
      generation,
      label: `Person ${generation}`,
      percent: Number(perPersonPercent || 0),
      isActive: true,
    };
  });
}

function buildParams(filters, page = 1) {
  const params = new URLSearchParams({ page: String(page), pageSize: '20' });
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      params.set(key, String(value).trim());
    }
  });
  return params.toString();
}

export default function IncomeGenerationAdminScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const headers = useMemo(() => ({ Authorization: `Bearer ${userInfo.token}` }), [userInfo.token]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overview, setOverview] = useState(null);
  const [settings, setSettings] = useState(null);
  const [ledger, setLedger] = useState({ rows: [], page: 1, pages: 1, total: 0, summary: {} });
  const [logs, setLogs] = useState({ logs: [], page: 1, pages: 1, total: 0, actions: [] });
  const [ledgerFilters, setLedgerFilters] = useState(emptyLedgerFilters);
  const [logFilters, setLogFilters] = useState(emptyLogFilters);
  const [simulatorForm, setSimulatorForm] = useState({ username: '', withdrawAmount: '' });
  const [simulator, setSimulator] = useState(null);
  const [simulatorLoading, setSimulatorLoading] = useState(false);

  const activeTotal = useMemo(() => (
    Number(settings?.perPersonPercent || 0) * Number(settings?.generationCount || 0)
  ), [settings]);

  const fetchOverview = useCallback(async () => {
    const { data } = await axios.get(api + '/api/users/admin/income-generation/overview', { headers });
    setOverview(data);
    setSettings({
      ...data.settings,
      withdrawPercent: data.settings?.withdrawPercent ?? 10,
      perPersonPercent: data.settings?.perPersonPercent ?? 2,
      levels: normalizeLevels(
        [],
        data.settings?.generationCount || 5,
        data.settings?.perPersonPercent ?? 2
      ),
    });
  }, [headers]);

  const fetchLedger = useCallback(async (page = 1, filters = emptyLedgerFilters) => {
    const { data } = await axios.get(
      api + `/api/users/admin/income-generation/ledger?${buildParams(filters, page)}`,
      { headers }
    );
    setLedger(data);
  }, [headers]);

  const fetchLogs = useCallback(async (page = 1, filters = emptyLogFilters) => {
    const { data } = await axios.get(
      api + `/api/users/admin/income-generation/logs?${buildParams(filters, page)}`,
      { headers }
    );
    setLogs(data);
  }, [headers]);

  useEffect(() => {
    const loadInitial = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchOverview(),
          fetchLedger(1, emptyLedgerFilters),
          fetchLogs(1, emptyLogFilters),
        ]);
      } catch (err) {
        toast.error(getError(err));
      } finally {
        setLoading(false);
      }
    };
    loadInitial();
  }, [fetchLedger, fetchLogs, fetchOverview]);

  const refreshAll = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchOverview(),
        fetchLedger(ledger.page || 1, ledgerFilters),
        fetchLogs(logs.page || 1, logFilters),
      ]);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const updateGenerationCount = (value) => {
    const generationCount = Math.min(Math.max(parseInt(value) || 1, 1), 20);
    setSettings((current) => ({
      ...current,
      generationCount,
      levels: equalLevels(generationCount, current?.perPersonPercent || 2),
    }));
  };

  const updatePerPersonPercent = (value) => {
    const perPersonPercent = value;
    setSettings((current) => ({
      ...current,
      perPersonPercent,
      levels: equalLevels(current?.generationCount || 5, perPersonPercent),
    }));
  };

  const saveSettings = async () => {
    if (!settings) return;
    if (settings.isEnabled && Number(activeTotal.toFixed(2)) !== Number(Number(settings.withdrawPercent || 0).toFixed(2))) {
      toast.error(
        `The math must match: ${settings.perPersonPercent || 0}% x ${settings.generationCount || 0} people = ${settings.withdrawPercent || 0}% total charge.`
      );
      return;
    }
    try {
      setSaving(true);
      const { data } = await axios.put(
        api + '/api/users/admin/income-generation/settings',
        {
          ...settings,
          withdrawPercent: Number(settings.withdrawPercent || 0),
          perPersonPercent: Number(settings.perPersonPercent || 0),
          generationCount: Number(settings.generationCount || 0),
          levels: equalLevels(settings.generationCount, settings.perPersonPercent),
          minimumCharge: 0,
          allowBlockedReceivers: false,
        },
        { headers }
      );
      toast.success(data.message);
      setSettings({
        ...data.settings,
        levels: normalizeLevels(data.settings.levels, data.settings.generationCount, data.settings.perPersonPercent),
      });
      await Promise.all([fetchOverview(), fetchLogs(1)]);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSaving(false);
    }
  };

  const runSimulator = async (e) => {
    e.preventDefault();
    if (!simulatorForm.username.trim()) {
      toast.error('Please enter a username.');
      return;
    }
    if (!Number(simulatorForm.withdrawAmount || 0)) {
      toast.error('Please enter a withdraw amount.');
      return;
    }
    try {
      setSimulatorLoading(true);
      const params = new URLSearchParams({
        username: simulatorForm.username.trim(),
        withdrawAmount: String(simulatorForm.withdrawAmount || 0),
      });
      const { data } = await axios.get(
        api + `/api/users/admin/income-generation/simulator?${params.toString()}`,
        { headers }
      );
      setSimulator(data);
      if (!data.found) {
        toast.info(data.message || 'No exact user found.');
      }
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSimulatorLoading(false);
    }
  };

  const chooseSimulatorUsername = (username) => {
    setSimulatorForm((form) => ({ ...form, username }));
    setSimulator(null);
  };

  const applyLedgerFilters = async (e) => {
    e.preventDefault();
    try {
      await fetchLedger(1, ledgerFilters);
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const applyLogFilters = async (e) => {
    e.preventDefault();
    try {
      await fetchLogs(1, logFilters);
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const statusSummary = overview?.summary?.byStatus || {};
  const pending = statusSummary.Pending?.total || 0;
  const withdrawing = statusSummary.withdraw?.total || 0;
  const paid = statusSummary.withdraw_done?.total || 0;

  if (loading) {
    return (
      <div style={s.page}>
        <Helmet><title>Income Generation Control</title></Helmet>
        <div style={s.state}><i className="fa fa-spinner fa-spin" /> Loading Income Generation...</div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <Helmet><title>Income Generation Control</title></Helmet>

      <div style={s.header}>
        <div>
          <p style={s.eyebrow}>Admin Control</p>
          <h3 style={s.title}>Income Generation</h3>
          <p style={s.subtitle}>Control withdrawal-charge sharing, review every earning row, and search audit logs.</p>
        </div>
        <button type="button" style={s.refreshBtn} onClick={refreshAll}>
          <i className="fa fa-refresh" />
          Refresh Page
        </button>
      </div>

      <div style={s.stats}>
        <Stat icon={settings?.isEnabled ? 'fa-toggle-on' : 'fa-toggle-off'} label="Master Switch" value={settings?.isEnabled ? 'Enabled' : 'Disabled'} color={settings?.isEnabled ? '#198754' : '#dc3545'} />
        <Stat icon="fa-clock-o" label="Pending Income" value={fmt(pending)} color="#0d6efd" />
        <Stat icon="fa-hourglass-half" label="Withdraw Requested" value={fmt(withdrawing)} color="#f59e0b" />
        <Stat icon="fa-check-circle" label="Paid Income" value={fmt(paid)} color="#198754" />
      </div>

      <section style={s.panel}>
        <div style={s.panelHeader}>
          <div>
            <h5 style={s.panelTitle}>Income Rules</h5>
            <p style={s.panelText}>Simple rule: take a percent from the withdraw, then pay a smaller percent to each sponsor above the user.</p>
          </div>
          <button
            type="button"
            style={{ ...s.switchBtn, background: settings?.isEnabled ? '#dc3545' : '#198754' }}
            onClick={() => updateSetting('isEnabled', !settings?.isEnabled)}
          >
            <i className={`fa ${settings?.isEnabled ? 'fa-toggle-off' : 'fa-toggle-on'}`} />
            {settings?.isEnabled ? 'Disable Income Generation' : 'Enable Income Generation'}
          </button>
        </div>

        <div style={s.formulaBox}>
          <i className="fa fa-calculator" />
          <span>
            Current rule: take <strong>{fmt(settings?.withdrawPercent || 0)}%</strong> from each withdraw, pay <strong>{fmt(settings?.perPersonPercent || 0)}%</strong> to each person, for <strong>{settings?.generationCount || 0}</strong> people. Total payout is <strong>{fmt(activeTotal)}%</strong>.
          </span>
        </div>

        <div style={s.formGrid}>
          <label style={s.field}>Total Charge From Withdraw (%)
            <input
              style={s.input}
              type="number"
              min="0"
              max="99"
              step="0.01"
              value={settings?.withdrawPercent ?? 10}
              onChange={(e) => updateSetting('withdrawPercent', e.target.value)}
            />
            <span style={s.helpText}>Example: 10 means the user pays 10% charge from the withdraw amount.</span>
          </label>
          <label style={s.field}>Each Person Gets (%)
            <input
              style={s.input}
              type="number"
              min="0"
              max="99"
              step="0.01"
              value={settings?.perPersonPercent ?? 2}
              onChange={(e) => updatePerPersonPercent(e.target.value)}
            />
            <span style={s.helpText}>Example: 2 means each sponsor person receives 2% of the withdraw amount.</span>
          </label>
          <label style={s.field}>How Many People Get Paid
            <input
              style={s.input}
              type="number"
              min="1"
              max="20"
              value={settings?.generationCount || 5}
              onChange={(e) => updateGenerationCount(e.target.value)}
            />
            <span style={s.helpText}>Example: 5 means pay up to 5 sponsor people above the withdrawing user.</span>
          </label>
          <label style={{ ...s.field, gridColumn: '1 / -1' }}>Admin Note
            <textarea
              style={{ ...s.input, minHeight: 70, paddingTop: 10 }}
              value={settings?.notes || ''}
              onChange={(e) => updateSetting('notes', e.target.value)}
              placeholder="Short note for why these rules are set this way"
            />
          </label>
        </div>

        <div style={s.levelHeader}>
          <strong>Payment Preview</strong>
          <span style={{ ...s.percentPill, background: Number(activeTotal.toFixed(2)) === Number(Number(settings?.withdrawPercent || 0).toFixed(2)) ? '#d1e7dd' : '#fff3cd', color: Number(activeTotal.toFixed(2)) === Number(Number(settings?.withdrawPercent || 0).toFixed(2)) ? '#0f5132' : '#664d03' }}>
            {fmt(settings?.perPersonPercent || 0)}% x {settings?.generationCount || 0} people = {fmt(activeTotal)}%
          </span>
        </div>
        <div style={s.levelGrid}>
          {equalLevels(settings?.generationCount || 5, settings?.perPersonPercent || 0).map((level) => (
            <div key={level.generation} style={s.levelRow}>
              <span style={s.previewPerson}><i className="fa fa-user" /> {level.label}</span>
              <strong>{fmt(level.percent)}%</strong>
              <span>of withdraw</span>
            </div>
          ))}
        </div>

        <div style={s.actions}>
          <button type="button" style={s.primaryBtn} onClick={saveSettings} disabled={saving}>
            <i className={`fa ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`} />
            Save Income Rules
          </button>
        </div>
      </section>

      <section style={s.panel}>
        <div style={s.panelHeader}>
          <div>
            <h5 style={s.panelTitle}>Income Example Simulator</h5>
            <p style={s.panelText}>Type a real username and a withdraw amount to preview who would receive Income Generation.</p>
          </div>
          <span style={s.countPill}>Demo only</span>
        </div>
        <form style={s.simulatorForm} onSubmit={runSimulator}>
          <label style={s.field}>Username
            <input
              style={s.input}
              value={simulatorForm.username}
              onChange={(e) => {
                setSimulatorForm({ ...simulatorForm, username: e.target.value });
                setSimulator(null);
              }}
              placeholder="Enter exact username"
            />
          </label>
          <label style={s.field}>Withdraw Amount
            <input
              style={s.input}
              type="number"
              min="1"
              step="0.01"
              value={simulatorForm.withdrawAmount}
              onChange={(e) => {
                setSimulatorForm({ ...simulatorForm, withdrawAmount: e.target.value });
                setSimulator(null);
              }}
              placeholder="Example: 1000"
            />
          </label>
          <button type="submit" style={s.primaryBtn} disabled={simulatorLoading}>
            <i className={`fa ${simulatorLoading ? 'fa-spinner fa-spin' : 'fa-play-circle'}`} />
            Show Demo
          </button>
        </form>

        {simulator && !simulator.found && (
          <div style={s.suggestionBox}>
            <div style={s.suggestionTitle}><i className="fa fa-search" /> {simulator.message}</div>
            <div style={s.suggestionList}>
              {(simulator.suggestions || []).map((user) => (
                <button
                  type="button"
                  key={user._id || user.username}
                  style={s.suggestionBtn}
                  onClick={() => chooseSimulatorUsername(user.username)}
                >
                  <span style={s.avatar}>{String(user.username || '?')[0].toUpperCase()}</span>
                  <span>
                    <strong>{user.username}</strong>
                    <small>{user.name || 'User'}</small>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {simulator?.found && (
          <div style={s.simulatorResult}>
            <div style={s.sourceCard}>
              <div style={s.avatarLarge}>{String(simulator.sourceUser?.username || '?')[0].toUpperCase()}</div>
              <div>
                <div style={s.sourceTitle}>{simulator.sourceUser?.name || simulator.sourceUser?.username}</div>
                <div style={s.smallText}>Username: {simulator.sourceUser?.username}</div>
              </div>
            </div>
            <div style={s.demoStats}>
              <DemoStat icon="fa-money" label="Withdraw Amount" value={fmt(simulator.withdraw?.grossAmount)} />
              <DemoStat icon="fa-percent" label="Total Charge" value={`${fmt(simulator.withdraw?.charge)} (${fmt(simulator.withdraw?.chargeRate)}%)`} />
              <DemoStat icon="fa-handshake-o" label="Shared To People" value={fmt(simulator.summary?.totalDistributed)} />
              <DemoStat icon="fa-users" label="Receivers" value={`${simulator.summary?.totalReceivers || 0} paid, ${simulator.summary?.skipped || 0} skipped`} />
            </div>
            <div style={s.receiverFlow}>
              {(simulator.rows || []).map((row) => (
                <div key={row.generation} style={{ ...s.receiverCard, borderColor: row.skipped ? '#fecaca' : '#bbf7d0' }}>
                  <div style={{ ...s.receiverIcon, background: row.skipped ? '#fef2f2' : '#f0fdf4', color: row.skipped ? '#dc2626' : '#15803d' }}>
                    <i className={`fa ${row.skipped ? 'fa-minus-circle' : 'fa-check-circle'}`} />
                  </div>
                  <div style={s.receiverBody}>
                    <div style={s.receiverTop}>
                      <strong>{row.label}</strong>
                      <span style={{ ...s.statusPill, background: row.skipped ? '#fef2f2' : '#dcfce7', color: row.skipped ? '#991b1b' : '#166534' }}>
                        {row.status}
                      </span>
                    </div>
                    <div style={s.receiverName}>
                      {row.receiver?.username ? (
                        <>
                          <span style={s.avatarSmall}>{String(row.receiver.username || '?')[0].toUpperCase()}</span>
                          <span>{row.receiver.name || row.receiver.username} <small>({row.receiver.username})</small></span>
                        </>
                      ) : (
                        <span style={s.mutedText}>{row.reason || 'No receiver found'}</span>
                      )}
                    </div>
                    <div style={s.receiverMoney}>
                      <span>{fmt(row.percent)}% of withdraw</span>
                      <strong>{fmt(row.amount)}</strong>
                    </div>
                    {row.reason && row.receiver?.username && <div style={s.reasonText}>{row.reason}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section style={s.panel}>
        <div style={s.panelHeader}>
          <div>
            <h5 style={s.panelTitle}>Income Ledger</h5>
            <p style={s.panelText}>Search earning rows by receiver, source user, status, generation, and date.</p>
          </div>
          <span style={s.countPill}>{ledger.total || 0} rows</span>
        </div>
        <form style={s.filterGrid} onSubmit={applyLedgerFilters}>
          <input style={s.input} placeholder="Search details" value={ledgerFilters.search} onChange={(e) => setLedgerFilters({ ...ledgerFilters, search: e.target.value })} />
          <input style={s.input} placeholder="Receiver username" value={ledgerFilters.receiverUsername} onChange={(e) => setLedgerFilters({ ...ledgerFilters, receiverUsername: e.target.value })} />
          <input style={s.input} placeholder="Source username" value={ledgerFilters.sourceUsername} onChange={(e) => setLedgerFilters({ ...ledgerFilters, sourceUsername: e.target.value })} />
          <select style={s.input} value={ledgerFilters.status} onChange={(e) => setLedgerFilters({ ...ledgerFilters, status: e.target.value })}>
            <option value="">All status</option>
            <option value="Pending">Pending</option>
            <option value="withdraw">Withdraw Requested</option>
            <option value="withdraw_done">Paid</option>
          </select>
          <input style={s.input} placeholder="Generation" type="number" min="1" value={ledgerFilters.generation} onChange={(e) => setLedgerFilters({ ...ledgerFilters, generation: e.target.value })} />
          <input style={s.input} type="date" value={ledgerFilters.startDate} onChange={(e) => setLedgerFilters({ ...ledgerFilters, startDate: e.target.value })} />
          <input style={s.input} type="date" value={ledgerFilters.endDate} onChange={(e) => setLedgerFilters({ ...ledgerFilters, endDate: e.target.value })} />
          <button type="submit" style={s.secondaryBtn}><i className="fa fa-search" /> Search Ledger</button>
          <button type="button" style={s.secondaryBtn} onClick={() => { setLedgerFilters(emptyLedgerFilters); fetchLedger(1, emptyLedgerFilters); }}>
            <i className="fa fa-undo" /> Clear
          </button>
        </form>
        <DataTable
          columns={['Date', 'Receiver', 'Source User', 'Generation', 'Percent', 'Amount', 'Source Withdraw', 'Status']}
          rows={ledger.rows}
          renderRow={(row) => [
            fmtDate(row.createdAt),
            row.receiverUsername || row.receiverId?.username || 'Unknown',
            row.withdrawUsername || row.withdrawId?.username || 'Unknown',
            `Gen ${row.generation || '-'}`,
            `${fmt(row.percent || 0)}%`,
            fmt(row.amount),
            fmt(row.sourceWithdrawAmount || row.sourceWithdrawId?.grossAmount || 0),
            readableStatus(row.status),
          ]}
        />
        <Pager page={ledger.page} pages={ledger.pages} onPage={(page) => fetchLedger(page, ledgerFilters)} />
      </section>

      <section style={s.panel}>
        <div style={s.panelHeader}>
          <div>
            <h5 style={s.panelTitle}>Audit Logs</h5>
            <p style={s.panelText}>Every setting change, creation, skip, withdraw request, and payout is recorded here.</p>
          </div>
          <span style={s.countPill}>{logs.total || 0} logs</span>
        </div>
        <form style={s.filterGrid} onSubmit={applyLogFilters}>
          <input style={s.input} placeholder="Search log, IP, reason" value={logFilters.search} onChange={(e) => setLogFilters({ ...logFilters, search: e.target.value })} />
          <input style={s.input} placeholder="Any username" value={logFilters.username} onChange={(e) => setLogFilters({ ...logFilters, username: e.target.value })} />
          <select style={s.input} value={logFilters.action} onChange={(e) => setLogFilters({ ...logFilters, action: e.target.value })}>
            <option value="">All actions</option>
            {(logs.actions || []).map((action) => (
              <option key={action._id} value={action._id}>{action._id} ({action.count})</option>
            ))}
          </select>
          <input style={s.input} type="date" value={logFilters.startDate} onChange={(e) => setLogFilters({ ...logFilters, startDate: e.target.value })} />
          <input style={s.input} type="date" value={logFilters.endDate} onChange={(e) => setLogFilters({ ...logFilters, endDate: e.target.value })} />
          <button type="submit" style={s.secondaryBtn}><i className="fa fa-search" /> Search Logs</button>
          <button type="button" style={s.secondaryBtn} onClick={() => { setLogFilters(emptyLogFilters); fetchLogs(1, emptyLogFilters); }}>
            <i className="fa fa-undo" /> Clear
          </button>
        </form>
        <DataTable
          columns={['Date', 'Action', 'Actor', 'Receiver', 'Source User', 'Amount', 'IP', 'Note']}
          rows={logs.logs}
          renderRow={(log) => [
            fmtDate(log.createdAt),
            log.actionLabel || log.action,
            log.actorName || 'System',
            log.receiverUsername || '-',
            log.sourceUsername || '-',
            fmt(log.amount),
            log.ip || 'Unknown',
            log.comment || log.reason || '-',
          ]}
        />
        <Pager page={logs.page} pages={logs.pages} onPage={(page) => fetchLogs(page, logFilters)} />
      </section>
    </div>
  );
}

function readableStatus(status) {
  if (status === 'Pending') return 'Pending';
  if (status === 'withdraw') return 'Withdraw Requested';
  if (status === 'withdraw_done') return 'Paid';
  return status || 'Unknown';
}

function Stat({ icon, label, value, color }) {
  return (
    <div style={s.stat}>
      <div style={{ ...s.statIcon, background: `${color}18`, color }}><i className={`fa ${icon}`} /></div>
      <div>
        <div style={s.statLabel}>{label}</div>
        <div style={s.statValue}>{value}</div>
      </div>
    </div>
  );
}

function DemoStat({ icon, label, value }) {
  return (
    <div style={s.demoStat}>
      <i className={`fa ${icon}`} />
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function DataTable({ columns, rows, renderRow }) {
  return (
    <div style={s.tableWrap}>
      <table style={s.table}>
        <thead>
          <tr>
            {columns.map((column) => <th key={column} style={s.th}>{column}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td style={s.emptyTd} colSpan={columns.length}>No records found.</td></tr>
          ) : rows.map((row) => (
            <tr key={row._id} style={s.tr}>
              {renderRow(row).map((value, index) => (
                <td key={`${row._id}-${columns[index]}`} style={s.td}>{value}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pager({ page, pages, onPage }) {
  if (!pages || pages <= 1) return null;
  return (
    <div style={s.pager}>
      <button type="button" style={s.secondaryBtn} disabled={page <= 1} onClick={() => onPage(page - 1)}>
        <i className="fa fa-arrow-left" /> Previous
      </button>
      <span style={s.pageText}>Page {page} of {pages}</span>
      <button type="button" style={s.secondaryBtn} disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Next <i className="fa fa-arrow-right" />
      </button>
    </div>
  );
}

const s = {
  page: {
    padding: 20,
    maxWidth: 1280,
    margin: '0 auto',
    color: '#111827',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16 },
  eyebrow: { margin: 0, color: '#0d6efd', fontSize: 12, fontWeight: 900, textTransform: 'uppercase' },
  title: { margin: '2px 0', fontWeight: 900 },
  subtitle: { margin: 0, color: '#6b7280' },
  refreshBtn: {
    border: '1px solid #d1d5db',
    borderRadius: 8,
    background: '#fff',
    color: '#374151',
    padding: '10px 14px',
    fontWeight: 800,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 16 },
  stat: { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14 },
  statIcon: { width: 40, height: 40, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  statLabel: { color: '#6b7280', fontSize: 12, fontWeight: 900, textTransform: 'uppercase' },
  statValue: { fontSize: 20, fontWeight: 900 },
  panel: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 },
  panelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 },
  panelTitle: { margin: 0, fontWeight: 900 },
  panelText: { margin: '4px 0 0', color: '#6b7280', fontSize: 13 },
  switchBtn: { border: 'none', borderRadius: 8, color: '#fff', padding: '10px 14px', display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 900 },
  formulaBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    background: '#eff6ff',
    color: '#1d4ed8',
    borderRadius: 8,
    padding: 12,
    marginBottom: 14,
    fontSize: 14,
    fontWeight: 700,
  },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 },
  filterGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 800, color: '#374151' },
  checkField: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 800, color: '#374151', minHeight: 40, marginTop: 22 },
  optionBox: { display: 'flex', flexDirection: 'column', gap: 6 },
  helpText: { color: '#6b7280', fontSize: 12, fontWeight: 700, lineHeight: 1.4 },
  input: { minHeight: 40, border: '1px solid #d1d5db', borderRadius: 8, padding: '0 11px', fontSize: 14, background: '#fff', color: '#111827' },
  smallText: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  levelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 16, marginBottom: 10 },
  percentPill: { borderRadius: 999, padding: '6px 10px', fontWeight: 900, fontSize: 12 },
  levelGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 10 },
  levelRow: { border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, display: 'grid', gridTemplateColumns: '1fr 76px 96px', alignItems: 'center', gap: 8 },
  previewPerson: { display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 900, color: '#374151' },
  inlineCheck: { display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 800, color: '#374151' },
  percentInput: { minHeight: 36, border: '1px solid #d1d5db', borderRadius: 8, padding: '0 9px' },
  actions: { display: 'flex', justifyContent: 'flex-end', marginTop: 14 },
  primaryBtn: { border: 'none', borderRadius: 8, background: '#0d6efd', color: '#fff', padding: '10px 14px', display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 900 },
  secondaryBtn: { border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', padding: '9px 12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontWeight: 800, minHeight: 40 },
  countPill: { background: '#eef2ff', color: '#3730a3', borderRadius: 999, padding: '6px 10px', fontSize: 12, fontWeight: 900 },
  simulatorForm: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, alignItems: 'end' },
  suggestionBox: { marginTop: 14, border: '1px solid #bfdbfe', background: '#eff6ff', borderRadius: 8, padding: 12 },
  suggestionTitle: { display: 'flex', alignItems: 'center', gap: 8, color: '#1d4ed8', fontWeight: 900, marginBottom: 10 },
  suggestionList: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 },
  suggestionBtn: { border: '1px solid #dbeafe', background: '#fff', borderRadius: 8, padding: 10, display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', cursor: 'pointer', color: '#111827' },
  avatar: { width: 34, height: 34, borderRadius: 8, background: '#e0f2fe', color: '#0369a1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900 },
  avatarLarge: { width: 50, height: 50, borderRadius: 8, background: '#e0f2fe', color: '#0369a1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 900 },
  avatarSmall: { width: 26, height: 26, borderRadius: 8, background: '#e0f2fe', color: '#0369a1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900 },
  simulatorResult: { marginTop: 16, display: 'grid', gap: 14 },
  sourceCard: { border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, display: 'flex', alignItems: 'center', gap: 12, background: '#f9fafb' },
  sourceTitle: { fontSize: 18, fontWeight: 900, color: '#111827' },
  demoStats: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 },
  demoStat: { border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, display: 'grid', gap: 4, background: '#fff' },
  receiverFlow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 10 },
  receiverCard: { border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, display: 'flex', gap: 10, background: '#fff' },
  receiverIcon: { width: 34, height: 34, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' },
  receiverBody: { flex: 1, minWidth: 0 },
  receiverTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 },
  receiverName: { display: 'flex', alignItems: 'center', gap: 8, minHeight: 28, fontWeight: 800, color: '#374151' },
  receiverMoney: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, color: '#6b7280', fontSize: 13, marginTop: 10 },
  statusPill: { borderRadius: 999, padding: '4px 8px', fontSize: 11, fontWeight: 900, whiteSpace: 'nowrap' },
  mutedText: { color: '#6b7280', fontSize: 13 },
  reasonText: { color: '#991b1b', background: '#fef2f2', borderRadius: 8, padding: 8, marginTop: 10, fontSize: 12, fontWeight: 800 },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 980 },
  th: { textAlign: 'left', padding: '12px 13px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontSize: 12, fontWeight: 900, textTransform: 'uppercase' },
  td: { padding: '12px 13px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  tr: { background: '#fff' },
  emptyTd: { padding: 24, textAlign: 'center', color: '#6b7280' },
  pager: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 14 },
  pageText: { fontWeight: 800, color: '#374151' },
  state: { padding: 50, textAlign: 'center', color: '#374151', fontWeight: 900, display: 'flex', justifyContent: 'center', gap: 8 },
};
